import {
  Icon,
  IconButton,
  useDisclosure,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useBoolean,
  Box,
  Portal,
} from '@wegroup/design-system';
import React, { useRef } from 'react';
import { Change } from '../../../../../../../types/Form';
import { getHubspotChatElement } from '../../../../../../common/src/hooks/support-chat/useMoveHubspotBubble';
import useTracking from '../../../../../../common/src/hooks/tracking/useTracking';
import { compareProps } from '../../../../../../tarifications/src/legacy/services/proFlowService';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import useTagContent from '../../../ui/common/hooks/useTagContent';
import useToggleCompanyOverview from '../../../ui/company-scan/hooks/useToggleCompanyOverview';
import NoteInput from './NoteInput';

interface Props {
  question: FlowsQuestion;
  value: string;
  onChange: (e: Change | React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}

const NotesPopover: React.FC<React.PropsWithChildren<Props>> = ({
  question,
  value,
  onChange,
  onBlur,
}) => {
  const { trackEvent } = useTracking();
  const { isOpen, onClose, onToggle, onOpen } = useDisclosure();
  const [isPinned, setIsPinned] = useBoolean();
  const notesInputRef = useRef<HTMLTextAreaElement>(null);

  // Other element which is the HELP button (campaigns) or INTERCOM button
  const element = getHubspotChatElement();

  const tagName = useTagContent(question.tag);
  const { isOverview } = useToggleCompanyOverview();

  const handleOnPinClick = () => {
    // Makes sure when clicking the button it is always open
    onOpen();
    setIsPinned.toggle();
  };
  const handleOnClose = () => {
    setIsPinned.off();
    onClose();
  };

  const handleToggle = () => {
    if (!isOpen) {
      trackEvent('Opened Notes in Company Scan');
    }
    onToggle();
  };

  return (
    <Popover
      placement="top-end"
      isOpen={!isOverview && (isPinned || isOpen)}
      onClose={onClose}
      initialFocusRef={notesInputRef}
    >
      <PopoverTrigger>
        <Box pos="fixed" right={element ? '24' : '8'} bottom={'92px'}>
          <IconButton
            size="lg"
            bg="white"
            border="1px"
            borderColor="gray.50"
            boxShadow="level1"
            borderRadius="full"
            icon={<Icon name="GeneralOthersNote" />}
            aria-label="Notes"
            onClick={handleToggle}
            variant="grayed"
          />
          {value && (
            <Box
              borderRadius="full"
              bg="primary.500"
              width="3"
              height="3"
              pos="absolute"
              left="75%"
              bottom="75%"
            />
          )}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          rootProps={{ zIndex: 'modal' }}
          borderRadius="sm"
          minW="sm"
          boxShadow="level4"
          border="1px"
          borderColor="gray.ultraLight"
        >
          <NoteInput
            notesRef={notesInputRef}
            ref={notesInputRef}
            name={question.id}
            title={tagName}
            value={value}
            onChange={(e) =>
              onChange({ name: question.id, value: e.target.value })
            }
            onBlur={onBlur}
            onClose={handleOnClose}
            isPinned={isPinned}
            onPinClick={handleOnPinClick}
          />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default React.memo(
  NotesPopover,
  (prevProps, nextProps) =>
    compareProps(prevProps.question, nextProps.question) &&
    prevProps.value === nextProps.value,
);
